import React, { useEffect, useState } from 'react';
import './TourStep.scss';
import { useTourStore } from 'src/store/tour';
import { cn } from '@utils/index';

interface Position {
  top: number;
  left: number;
  pointerDirection: 'top' | 'right' | 'bottom' | 'left';
}

interface StepPositioning {
  align: 'top' | 'center';
  offset?: number;
}

export const TourStep: React.FC = () => {
  const {
    currentStep,
    totalSteps,
    previousStep,
    nextStep,
    closeTour,
  } = useTourStore();

  const [position, setPosition] = useState<Position>({ top: 0, left: 0, pointerDirection: 'left' });

  const isLastStep = currentStep === totalSteps;

  interface StepData {
    description: string;
    targetId?: string;
    targetSelector?: string;
    pointerDirection: 'top' | 'right' | 'bottom' | 'left';
    positioning?: StepPositioning;
  }

  const steps: Record<number, StepData> = {
    1: {
      description: 'Welcome to the tour! This will guide you through the interface.',
      targetId: 'step-parent',
      pointerDirection: 'left'
    },
    2: {
      description: 'This area will describe the page you are on and highlight any important details that you should know.',
      targetId: 'add-header-left-text',
      pointerDirection: 'left',
      positioning: {
        align: 'top',
        offset: 40,
      }
    },
    3: {
      description: 'Page navigation will appear in this area. You can use it to go forward or backward through the application.',
      targetId: 'app-header-left-nav',
      pointerDirection: 'left'
    },
    4: {
      description: "This is your main workspace — it's where you'll read important info, enter your details, and make your selections.",
      targetId: 'childrenBlock',
      pointerDirection: 'left',
      positioning: {
        align: 'center',
      }
    },
    5: {
      description: 'This is your progress bar. It is a handy little guide to show you where you are in your application process.',
      targetId: 'app-footer',
      pointerDirection: 'bottom'
    },
    6: {
      description: "Finally, meet Alia — your personal benefits assistant. She's here to answer your questions and help you understand your benefits. And yes, she's pretty smart!",
      targetSelector: '.assistant-sidebar.copilotKitSidebar',
      pointerDirection: 'right'
    },
  };

  const currentStepData = steps[currentStep as keyof typeof steps];
  const description = currentStepData?.description || '';

  const calculatePosition = () => {
    if (!currentStepData) {
      setPosition({
        top: window.innerHeight / 2 - 100,
        left: window.innerWidth / 2 - 160,
        pointerDirection: 'left'
      });
      return;
    }

    const targetElement = currentStepData.targetId 
      ? document.getElementById(currentStepData.targetId)
      : document.querySelector(currentStepData.targetSelector || '');

    if (!targetElement) {
      setPosition({
        top: window.innerHeight / 2 - 100,
        left: window.innerWidth / 2 - 160,
        pointerDirection: currentStepData.pointerDirection
      });
      return;
    }

    const rect = targetElement.getBoundingClientRect();
    
    if (rect.width === 0 || rect.height === 0) {
      setTimeout(calculatePosition, 100);
      return;
    }

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    // Примерные размеры тултипа
    const tooltipWidth = 320;
    const tooltipHeight = 200;

    let top = rect.top + scrollTop;
    let left = rect.left + scrollLeft;

    switch (currentStepData.pointerDirection) {
      case 'left':
        if (currentStepData.positioning?.align === 'top') {
          // Для 'add-header-left-text': правый верхний край + 40px вниз
          top = rect.top + scrollTop + (currentStepData.positioning.offset || 0);
        } else {
          // Для остальных: правый край, середина дива
          top = rect.top + scrollTop + (rect.height / 2) - (tooltipHeight / 2);
        }
        if (currentStepData.positioning?.align === 'center') {
          left = rect.right + scrollLeft + 26;
        } else {
          // Позиционируем справа от элемента
          left = rect.right + scrollLeft + 54;
        }
        break;
      case 'right':
        // Позиционируем слева от элемента (левый край, середина дива)
        left = rect.left + scrollLeft - tooltipWidth - 26;
        top = rect.top + scrollTop + (rect.height / 2) - (tooltipHeight / 2);
        break;
      case 'bottom':
        // Позиционируем сверху от элемента (верхний край, середина дива)
        left = rect.left + scrollLeft + (rect.width / 2) - (tooltipWidth / 2);
        top = rect.top + scrollTop - tooltipHeight - 10;
        break;
    }

    setPosition({
      top: Math.max(20, Math.min(top, window.innerHeight - tooltipHeight - 20)),
      left: Math.max(20, Math.min(left, window.innerWidth - tooltipWidth - 20)),
      pointerDirection: currentStepData.pointerDirection
    });
  };

  useEffect(() => {
    setTimeout(calculatePosition, 100);

    window.addEventListener('resize', calculatePosition);
    window.addEventListener('scroll', calculatePosition);

    return () => {
      window.removeEventListener('resize', calculatePosition);
      window.removeEventListener('scroll', calculatePosition);
    };
  }, [currentStep, currentStepData]);

  if (!position.top && !position.left) return null;

  return (
    <div 
      className={`tour-step tour-step--${position.pointerDirection}`} 
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        zIndex: 10001
      }}
    >
      <div className="tour-step__container">
        <button 
          className="tour-step__close" 
          onClick={closeTour}
          aria-label="Close tour step"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path 
              d="M18 6L6 18M6 6L18 18" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div className="tour-step__content">
          <p className="tour-step__description">{description}</p>
        </div>

        <div className="tour-step__footer">
          <div className="tour-step__actions">
            <button 
              className="tour-step__button tour-step__button--previous"
              onClick={previousStep}
            >
              Previous
            </button>
            <button 
              className="tour-step__button tour-step__button--next"
              onClick={isLastStep ? closeTour : nextStep}
            >
              {isLastStep ? 'Done' : 'Next'}
            </button>
          </div>
        </div>

        <div className={
          cn("tour-step__progress", `tour-step__progress--step${currentStep}`)
        }>
          <span className="tour-step__step-indicator">
            {currentStep}/{totalSteps}
          </span>
        </div>
      </div>
    </div>
  );
};
